import { useNavigate, useSearchParams } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router';
import { toast } from 'sonner';

const addServiceSchema = z.object({
  service_instance_name: z.string().min(1, 'Service name is required').max(200),
  service_definition_id: z.string().min(1, 'Please select a service definition'),
});

type AddServiceForm = z.infer<typeof addServiceSchema>;

export function AddServicePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedDefinition = searchParams.get('definition') || '';

  const { data: definitionsData } = useQuery({
    queryKey: ['service-definitions'],
    queryFn: () => api.get('/api/services/definition/list').then((r) => r.data),
  });

  const definitions = definitionsData?.query?.data ?? [];

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<AddServiceForm>({
    resolver: zodResolver(addServiceSchema),
    defaultValues: {
      service_definition_id: preselectedDefinition,
    },
  });

  const addMutation = useMutation({
    mutationFn: (data: AddServiceForm) => api.post('/api/services/add', data),
    onSuccess: (response) => {
      toast.success('Service created successfully');
      const serviceId = response.data?.query?.[0]?.service_instance_id || response.data?.identity;
      if (serviceId) {
        navigate(`/services/${serviceId}`);
      } else {
        navigate('/services');
      }
    },
    onError: () => toast.error('Failed to create service'),
  });

  async function onSubmit(data: AddServiceForm) {
    addMutation.mutate(data);
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/services/catalog">
          <Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button>
        </Link>
        <h1 className="text-2xl font-bold">Add Service</h1>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>Create New Service</CardTitle>
          <CardDescription>Select a service definition and configure your new service instance.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="service_instance_name">Service Name</Label>
              <Input
                id="service_instance_name"
                placeholder="My integration service"
                {...register('service_instance_name')}
              />
              {errors.service_instance_name && (
                <p className="text-sm text-red-500">{errors.service_instance_name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Service Definition</Label>
              <Select
                defaultValue={preselectedDefinition}
                onValueChange={(value) => setValue('service_definition_id', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a service definition" />
                </SelectTrigger>
                <SelectContent>
                  {definitions.map((def: any) => (
                    <SelectItem key={def.service_definition_id} value={def.service_definition_id}>
                      {def.source_app_name} → {def.target_app_name} ({def.service_definition_name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.service_definition_id && (
                <p className="text-sm text-red-500">{errors.service_definition_id.message}</p>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" disabled={isSubmitting || addMutation.isPending}>
                {addMutation.isPending ? 'Creating...' : 'Create Service'}
              </Button>
              <Link to="/services">
                <Button type="button" variant="outline">Cancel</Button>
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}